import { Illustration, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useEffect } from "react";

export interface TimesheetSubmittedDialogProps {
  modalState: UseModalState;
  isCaliforniaFacility: boolean;
  onContinue: () => void;
}

export function TimesheetSubmittedDialog({
  modalState,
  isCaliforniaFacility,
  onContinue,
}: TimesheetSubmittedDialogProps) {
  const { startTimer, secondsLeft, resetTimer } = useCountdownTimer({
    durationInSeconds: 5,
  });

  useEffect(() => {
    if (modalState.modalIsOpen) {
      resetTimer();
      startTimer();
    }
  }, [modalState.modalIsOpen, resetTimer, startTimer]);

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) => theme.palette.background.tertiary,
        }}
      >
        <Stack direction="column" alignItems="center" spacing={7}>
          <Illustration type="shift-booked" />
          <Title semibold component="h5" textAlign="center">
            {isCaliforniaFacility ? "Timesheet Signed!" : "Timesheet Submitted!"}
          </Title>
          <Stack spacing={9}>
            <Title component="h3" textAlign="center">
              Please return this device to the Clipboard professional
            </Title>
            {isCaliforniaFacility ? (
              <Text
                variant="body1"
                textAlign="center"
                color={(theme) => theme.palette.text.secondary}
              >
                They will finish submitting the timesheet.
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogFooter
        backgroundColor={(theme) => theme.palette.background.tertiary}
        orientation="vertical"
      >
        <Button
          fullWidth
          disabled={secondsLeft > 0}
          variant="contained"
          size="large"
          onClick={onContinue}
        >
          {secondsLeft > 0 ? `Continue in ${secondsLeft}s` : "Continue"}
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
